import { useCallback, useMemo } from 'react';

import { Permission } from '@bookora/shared';
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import { branchKey, invalidateBranchAwareQueries, queryKeys } from '@/api/query-keys';
import { useAuth } from '@/auth/token-store';
import { toast } from '@/lib/feedback';
import { useCan } from '@/permissions/use-can';

import { useActiveBranch } from './active-branch-store';
import { checkBranchSlug, fetchBranchUsage, fetchBranches, fetchMyBranches } from './api';
import type {
  BranchResponse,
  BranchSlugAvailability,
  BranchUsageResponse,
  MyBranch,
  MyBranchesResponse,
} from './contract';

/**
 * ════════════════════════════════════════════════════════════════════════════
 *  WHICH BRANCH THE PANEL IS LOOKING AT
 * ════════════════════════════════════════════════════════════════════════════
 *
 * One read decides everything: `GET /branches/mine` — the live branches this
 * caller works at (every branch for a `branch.view_all` holder), primary
 * first. From it:
 *
 *  · `isMulti` — more than one branch to choose from. Only then is there a
 *    switcher, a «شعبه» column, a «شعبه» select in a form or a branch chip on
 *    an event. A single-branch clinic (every basic/pro clinic) never sees the
 *    word «شعبه» anywhere.
 *  · `activeBranchId` — the switcher's choice, VALIDATED against that list:
 *    a stored id from last week that is now archived, or an id this caller no
 *    longer works at, falls back rather than sending a request the server's
 *    `BranchScopeGuard` answers with 403.
 *  · `scope` — what a branch-aware READ sends as `branchId`: the active branch,
 *    or nothing for «همهٔ شعبه‌ها» (the server then answers for every branch
 *    this caller may see — the scope is the server's, never the panel's).
 *  · `key` — the {@link branchKey} segment a branch-aware query key carries.
 *  · `defaultWriteBranchId` — where a new record goes when the form does not
 *    ask: the active branch, else the primary among the caller's branches.
 *
 * The switcher is a VIEW choice. It narrows nothing the server would not
 * narrow anyway, and it widens nothing: a receptionist restricted to branch A
 * who types branch B's id into a URL gets 403 BRANCH_FORBIDDEN.
 */

export interface BranchContext {
  /** `GET /branches/mine` answered (a failure reads as "single branch"). */
  ready: boolean;
  /** The caller's live branches, primary first. */
  branches: MyBranch[];
  isMulti: boolean;
  /** The caller may pick «همهٔ شعبه‌ها». */
  allowAll: boolean;
  /** A restricted caller with no branch at all — they see nothing branch-scoped. */
  unassigned: boolean;
  /** The chosen branch, or null for «همهٔ شعبه‌ها» (and for a single-branch caller). */
  activeBranchId: string | null;
  activeBranch: MyBranch | null;
  /** `branchId` for a branch-aware read; undefined = every branch the caller sees. */
  scope: string | undefined;
  /** The query-key segment of branch-aware keys. */
  key: string;
  /** Where a new record lands when its form does not ask. */
  defaultWriteBranchId: string | null;
  nameOf: (branchId: string | null | undefined) => string | null;
  setActive: (branchId: string | null) => void;
}

const MINE_STALE = 5 * 60_000;

export function useMyBranches(): UseQueryResult<MyBranchesResponse> {
  const { activeClinicId, isAuthenticated } = useAuth();
  return useQuery({
    queryKey: queryKeys.branches.mine(activeClinicId),
    queryFn: ({ signal }) => fetchMyBranches(signal),
    enabled: isAuthenticated && Boolean(activeClinicId),
    staleTime: MINE_STALE,
    retry: false,
  });
}

/** The pure part of {@link useBranchContext}, exported for its tests. */
export function resolveBranchContext(
  mine: MyBranchesResponse | undefined,
  storedBranchId: string | null,
): Omit<BranchContext, 'ready' | 'setActive' | 'nameOf'> {
  const branches = mine?.branches ?? [];
  const isMulti = branches.length > 1;
  const allowAll = Boolean(mine?.canViewAll) || isMulti;
  const primary = branches.find((b) => b.isPrimary) ?? branches[0] ?? null;
  const stored = storedBranchId ? (branches.find((b) => b.id === storedBranchId) ?? null) : null;

  // One branch: that branch is where everything happens, but a read sends no
  // `branchId` — the server scopes the caller to it anyway.
  if (!isMulti) {
    const only = branches[0] ?? null;
    return {
      branches,
      isMulti: false,
      allowAll,
      unassigned: mine !== undefined && branches.length === 0 && !mine.canViewAll,
      activeBranchId: null,
      activeBranch: null,
      scope: undefined,
      key: branchKey(null),
      defaultWriteBranchId: only?.id ?? null,
    };
  }

  // Several: the stored choice when it is still one of them; otherwise
  // «همهٔ شعبه‌ها» when allowed, else the primary.
  const active = stored ?? (allowAll ? null : primary);
  return {
    branches,
    isMulti: true,
    allowAll,
    unassigned: false,
    activeBranchId: active?.id ?? null,
    activeBranch: active,
    scope: active?.id,
    key: branchKey(active?.id ?? null),
    defaultWriteBranchId: active?.id ?? primary?.id ?? null,
  };
}

export function useBranchContext(): BranchContext {
  const mine = useMyBranches();
  const { branchId: stored, setBranch } = useActiveBranch();
  const resolved = useMemo(() => resolveBranchContext(mine.data, stored), [mine.data, stored]);
  const nameOf = useCallback(
    (branchId: string | null | undefined): string | null =>
      branchId ? (resolved.branches.find((b) => b.id === branchId)?.name ?? null) : null,
    [resolved.branches],
  );
  return {
    ...resolved,
    ready: mine.isSuccess || mine.isError,
    nameOf,
    setActive: setBranch,
  };
}

/**
 * Switches the active branch and refetches the branch-aware caches of this
 * clinic — ONLY those (`BRANCH_AWARE_ROOTS`). Never `clear()`: that is the
 * clinic switch's tool; the patient file, price list and settings are
 * clinic-wide and stay warm.
 */
export function useSwitchBranch(): (branchId: string | null) => void {
  const { activeClinicId } = useAuth();
  const queryClient = useQueryClient();
  const { branchId: current, setBranch } = useActiveBranch();
  return useCallback(
    (branchId: string | null) => {
      if (branchId === current) return;
      setBranch(branchId);
      void invalidateBranchAwareQueries(queryClient, activeClinicId);
    },
    [activeClinicId, current, queryClient, setBranch],
  );
}

/* -------------------------------------------------------------------------- */
/* Settings → «شعبه‌ها»                                                        */
/* -------------------------------------------------------------------------- */

export function useBranches(includeArchived: boolean): UseQueryResult<BranchResponse[]> {
  const { activeClinicId } = useAuth();
  const canView = useCan(Permission.SETTINGS_VIEW);
  return useQuery({
    queryKey: queryKeys.branches.list(activeClinicId, includeArchived),
    queryFn: ({ signal }) => fetchBranches(includeArchived, signal),
    enabled: canView && Boolean(activeClinicId),
    staleTime: 60_000,
  });
}

export function useBranchUsage(): UseQueryResult<BranchUsageResponse> {
  const { activeClinicId } = useAuth();
  const canView = useCan(Permission.SETTINGS_VIEW);
  return useQuery({
    queryKey: queryKeys.branches.usage(activeClinicId),
    queryFn: ({ signal }) => fetchBranchUsage(signal),
    enabled: canView && Boolean(activeClinicId),
    staleTime: 60_000,
  });
}

/** Whether a typed slug is free (debounced by the caller; skipped until it is well-formed). */
export function useBranchSlugCheck(
  slug: string,
  exceptBranchId: string | null,
  enabled: boolean,
): UseQueryResult<BranchSlugAvailability> {
  const { activeClinicId } = useAuth();
  const canManage = useCan(Permission.BRANCH_MANAGE);
  return useQuery({
    queryKey: queryKeys.branches.slug(activeClinicId, slug, exceptBranchId),
    queryFn: ({ signal }) => checkBranchSlug(slug, exceptBranchId, signal),
    enabled: enabled && canManage && Boolean(activeClinicId),
    staleTime: 30_000,
    retry: false,
  });
}

/**
 * A branch write, with its toast and the invalidation it needs. Coarse on
 * purpose: archiving re-homes staff and deactivates rooms and shifts; making a
 * branch primary moves the clinic's address; adding one changes the switcher,
 * every «شعبه» picker and the usage line at once.
 */
export function useBranchMutation<TArgs, TResult>(
  mutationFn: (args: TArgs) => Promise<TResult>,
  options: {
    successMessage: string | ((result: TResult) => string);
    onDone?: (result: TResult) => void;
    /** Takes the refusal instead of the default toast (e.g. to put it under a field). */
    onError?: (error: unknown) => void;
  },
) {
  const queryClient = useQueryClient();
  const { activeClinicId } = useAuth();
  return useMutation({
    mutationFn,
    onSuccess: (result: TResult) => {
      const text =
        typeof options.successMessage === 'function'
          ? options.successMessage(result)
          : options.successMessage;
      toast.success(text);
      void invalidateAfterBranchWrite(queryClient, activeClinicId);
      options.onDone?.(result);
    },
    onError: (error: unknown) => {
      if (options.onError) options.onError(error);
      else toast.error(error);
    },
  });
}

/** Everything a branch write can change on screen. */
export function invalidateAfterBranchWrite(
  queryClient: ReturnType<typeof useQueryClient>,
  clinicId: string | null,
): Promise<unknown> {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: queryKeys.branches.all(clinicId) }),
    queryClient.invalidateQueries({ queryKey: ['settings-section', clinicId] }),
    queryClient.invalidateQueries({ queryKey: ['staff-section', clinicId] }),
    queryClient.invalidateQueries({ queryKey: queryKeys.clinic(clinicId) }),
    invalidateBranchAwareQueries(queryClient, clinicId),
  ]);
}
